<template>
  <div class="row">
    <spinner-element :loading="loading" />

    <div class="row" v-if="locations.length">
      <div class="col-12" id="details">
        <div class="status-controls" v-if="!loading">
          <input
            v-model="search"
            type="text"
            class="form-control status-search"
            :placeholder="t('search-location')"
          />
          <div class="status-sort">
            <label class="status-sort-label">{{ t('sort-by') }}:</label>
            <select v-model="sortKey" class="form-select status-sort-select">
              <option value="name">{{ t('sort-by-name') }}</option>
              <option value="updated">{{ t('sort-by-updated') }}</option>
              <option value="availability">{{ t('sort-by-availability') }}</option>
            </select>
            <button
              type="button"
              class="btn-download status-sort-dir"
              :title="sortDir === 'asc' ? t('sort-asc') : t('sort-desc')"
              @click="toggleSortDir"
            >
              {{ sortDir === 'asc' ? '▲' : '▼' }}
            </button>
          </div>
        </div>

        <div class="row" id="road-info" v-if="!loading">
          <div
            v-for="location in visibleLocations"
            :key="location.id"
            class="content"
          >
            <div
              class="d-flex justify-content-between align-items-center table-container"
              :class="{ 'mb-2': expandedLocations[location.id] }"
            >
              <h5>
                {{ location.name }}
                <span class="location-meta">({{ location.shortName }}, {{ location.roadNumber }})</span>
                <span
                  v-if="overviewByLocation[location.id]"
                  :class="['status-chip', 'header-chip', getAvailabilityClass(overviewByLocation[location.id])]"
                >
                  {{ getAvailabilityLabel(overviewByLocation[location.id]) }}
                </span>
              </h5>
              <div class="location-actions">
                <button
                  v-if="canOpenRouter && routerUrl(location.id)"
                  type="button"
                  class="btn-download"
                  :title="t('ls-open-router')"
                  @click="openRouter(location.id)"
                >
                  {{ t('ls-open-router') }}
                </button>
                <button @click="toggleLocation(location.id)" class="btn-download">
                  {{ expandedLocations[location.id] ? t('collapse') : t('expand') }}
                </button>
              </div>
            </div>

            <div v-if="expandedLocations[location.id]">
              <div v-if="loadingByLocation[location.id]" class="status-line">
                {{ t('loading') }}
              </div>

              <div v-else-if="errorsByLocation[location.id]" class="status-line error">
                {{ errorsByLocation[location.id] }}
              </div>

              <div
                v-else-if="!statusByLocation[location.id] && !(historyByLocation[location.id] || []).length"
                class="status-line"
              >
                {{ t('no-data-from-location') }}
              </div>

              <div v-else>
                <h6 class="status-section-title">{{ t('ls-latest-entry') }}</h6>
                <table v-if="statusByLocation[location.id]" class="center-aligned table table-bordered">
                  <tbody>
                    <tr>
                      <td>
                        <table class="center-aligned table table-striped">
                          <thead>
                            <tr>
                              <th class="field-col">{{ t('ls-col-field') }}</th>
                              <th class="value-col">{{ t('ls-col-value') }}</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="row in toRows(statusByLocation[location.id])" :key="row.label">
                              <td>{{ row.label }}</td>
                              <td>{{ row.value }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div v-else class="status-line">{{ t('ls-no-latest-entry') }}</div>

                <div class="history-header">
                  <h6 class="status-section-title">{{ t('ls-history') }}</h6>
                  <button
                    v-if="(historyByLocation[location.id] || []).length"
                    type="button"
                    class="btn-download history-order-btn"
                    @click="toggleHistoryOrder(location.id)"
                  >
                    {{ t('sort-by-date') }}
                    {{ (historyOrderByLocation[location.id] || 'desc') === 'asc' ? '▲' : '▼' }}
                  </button>
                </div>
                <table
                  v-if="(historyByLocation[location.id] || []).length"
                  class="center-aligned table table-bordered history-table"
                >
                  <thead>
                    <tr>
                      <th class="lp-col">{{ t('ls-ordinal') }}</th>
                      <th>{{ t('ls-created-at') }}</th>
                      <th>{{ t('ls-status') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(entry, index) in historyByLocation[location.id]" :key="entry.id || index">
                      <td>{{ index + 1 }}</td>
                      <td>{{ formatDateTime(entry.createdAt) }}</td>
                      <td>
                        <span :class="['status-chip', getAvailabilityClass(entry)]">
                          {{ getAvailabilityLabel(entry) }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div v-else class="status-line">{{ t('ls-no-history') }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row vehicle-panel" v-if="!loading && locations.length && !visibleLocations.length">
      <div class="col-3" id="no-data-info">
        <div class="row details-row-field">
          {{ t('no-stations-match') }}
        </div>
      </div>
    </div>

    <div class="row vehicle-panel" v-if="!loading && !locations.length">
      <div class="col-3" id="no-data-info">
        <div class="row details-row-field">
          {{ t('no-data-for-selected-parameters') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SpinnerElement from "./SpinnerElement.vue";
import LocationStatusesService from "@/services/LocationStatuses";
import { useUserStore } from "@/stores/user.store";


export default {
  name: "LocationStatuses",
  components: {
    SpinnerElement,
  },
  data() {
    return {
      loading: false,
      locations: [],
      overviewByLocation: {},
      search: "",
      sortKey: "name",
      sortDir: "asc",
      expandedLocations: {},
      loadingByLocation: {},
      statusByLocation: {},
      historyByLocation: {},
      historyOrderByLocation: {},
      errorsByLocation: {},
    };
  },
  computed: {
    // Przycisk „Otwórz router" wymaga osobnego uprawnienia ROLE_16
    // (PermissionCode::LOCATION_STATUS_HISTORY_ROUTER). ROLE_15 daje sam wgląd
    // w zakładkę — bez ROLE_16 przycisk się nie pokazuje.
    canOpenRouter() {
      return useUserStore().hasAccess("LocationStatusHistoryRouter");
    },
    // Stacje po zastosowaniu wyszukiwania i sortowania (lista już zawiera tylko stacje z danymi).
    visibleLocations() {
      const term = this.search.trim().toLowerCase();
      let result = this.locations;

      if (term) {
        result = result.filter((location) => {
          const haystack = [location.name, location.shortName, location.roadNumber]
            .filter((value) => value !== null && value !== undefined)
            .join(" ")
            .toLowerCase();
          return haystack.includes(term);
        });
      }

      const dir = this.sortDir === "asc" ? 1 : -1;
      return [...result].sort((a, b) => dir * this.compareLocations(a, b));
    },
  },
  methods: {
    // Buduje adres panelu routera (router_ip + port 3001) na podstawie najnowszego statusu stacji.
    routerUrl(locationId) {
      const status = this.overviewByLocation[locationId];
      const rawIp = status && status.routerIp ? String(status.routerIp).trim() : "";
      if (!rawIp) {
        return null;
      }

      // INET z bazy może zawierać maskę (np. "10.0.0.1/24") — bierzemy sam adres.
      const ip = rawIp.split("/")[0];
      if (!ip) {
        return null;
      }

      return `http://${ip}:3001`;
    },
    openRouter(locationId) {
      // Druga zapora obok `v-if` — metoda jest publiczna, a bez ROLE_16 nikt nie ma
      // prawa otwierać panelu routera.
      if (!this.canOpenRouter) {
        return;
      }

      const url = this.routerUrl(locationId);
      if (!url) {
        return;
      }
      window.open(url, "_blank", "noopener");
    },
    compareLocations(a, b) {
      if (this.sortKey === "updated") {
        return this.lastUpdateTime(a) - this.lastUpdateTime(b);
      }
      if (this.sortKey === "availability") {
        return this.availabilityRank(a) - this.availabilityRank(b);
      }

      const aName = `${a.name || ""} ${a.shortName || ""}`.trim();
      const bName = `${b.name || ""} ${b.shortName || ""}`.trim();
      return aName.localeCompare(bName, "pl", { sensitivity: "base", numeric: true });
    },
    lastUpdateTime(location) {
      const status = this.overviewByLocation[location.id];
      const value = status && status.createdAt ? new Date(status.createdAt).getTime() : 0;
      return Number.isNaN(value) ? 0 : value;
    },
    availabilityRank(location) {
      // available < unavailable < no-data (rosnąco); kierunek odwraca sortDir.
      const order = { available: 0, unavailable: 1, "no-data": 2 };
      const state = this.getAvailabilityState(this.overviewByLocation[location.id]);
      return order[state] ?? 3;
    },
    toggleSortDir() {
      this.sortDir = this.sortDir === "asc" ? "desc" : "asc";
    },
    isNotFoundError(error) {
      return String(error ?? "").includes("404");
    },

    normalizeHistoryResponse(response) {
      if (Array.isArray(response)) {
        return response;
      }
      if (response && Array.isArray(response.items)) {
        return response.items;
      }
      if (response && Array.isArray(response.data)) {
        return response.data;
      }
      return [];
    },

    async loadLocations() {
      this.loading = true;
      try {
        const [locationsResult, overviewResult] = await Promise.allSettled([
          LocationStatusesService.getLocations(),
          LocationStatusesService.getOverview(),
        ]);

        const allLocations = locationsResult.status === "fulfilled" ? (locationsResult.value || []) : [];
        const overview = overviewResult.status === "fulfilled"
          ? this.normalizeHistoryResponse(overviewResult.value)
          : [];

        // Mapa: id lokalizacji -> najnowszy status (z przeglądu).
        const byId = {};
        overview.forEach((status) => {
          const id = status && status.location ? status.location.id : null;
          if (id !== null && id !== undefined) {
            byId[id] = status;
          }
        });
        this.overviewByLocation = byId;

        // Ukryj puste stacje — pokazujemy tylko te, które mają jakikolwiek wpis statusu.
        this.locations = allLocations.filter((location) =>
          Object.prototype.hasOwnProperty.call(byId, location.id)
        );
      } catch (error) {
        console.error("Błąd podczas pobierania lokalizacji:", error);
        this.locations = [];
        this.overviewByLocation = {};
      } finally {
        this.loading = false;
      }
    },
    async toggleLocation(locationId) {
      this.expandedLocations[locationId] = !this.expandedLocations[locationId];

      if (!this.expandedLocations[locationId]) {
        return;
      }

      if (Object.prototype.hasOwnProperty.call(this.statusByLocation, locationId)) {
        return;
      }

      await this.loadStatusForLocation(locationId);
    },
    async loadStatusForLocation(locationId) {
      this.loadingByLocation[locationId] = true;
      this.errorsByLocation[locationId] = "";

      // Najnowszy wpis mamy już z przeglądu (overview) — nie pobieramy go ponownie.
      this.statusByLocation[locationId] = this.overviewByLocation[locationId] || null;

      const orderDirection = this.historyOrderByLocation[locationId] || "desc";

      try {
        const response = await LocationStatusesService.getHistoryByLocation(locationId, {
          page: 1,
          limit: 100,
          orderDirection,
        });
        this.historyByLocation[locationId] = this.normalizeHistoryResponse(response);
      } catch (error) {
        if (this.isNotFoundError(error)) {
          this.historyByLocation[locationId] = [];
        } else {
          this.errorsByLocation[locationId] = this.t("ls-fetch-error");
        }
      } finally {
        this.loadingByLocation[locationId] = false;
      }
    },
    async toggleHistoryOrder(locationId) {
      const current = this.historyOrderByLocation[locationId] || "desc";
      const next = current === "desc" ? "asc" : "desc";
      this.historyOrderByLocation[locationId] = next;

      this.loadingByLocation[locationId] = true;
      try {
        const response = await LocationStatusesService.getHistoryByLocation(locationId, {
          page: 1,
          limit: 100,
          orderDirection: next,
        });
        this.historyByLocation[locationId] = this.normalizeHistoryResponse(response);
      } catch (error) {
        // Przy błędzie zostawiamy poprzednią historię bez zmian.
      } finally {
        this.loadingByLocation[locationId] = false;
      }
    },
    formatDateTime(value) {
      if (!value) {
        return "-";
      }

      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return String(value);
      }

      return date.toLocaleString();
    },
    formatValue(value) {
      return value === null || value === undefined || value === "" ? "-" : value;
    },
    normalizeStatusValue(value) {
      const numberValue = Number(value);
      return Number.isNaN(numberValue) ? null : numberValue;
    },
    getAvailabilityState(status) {
      if (!status || typeof status !== "object") {
        return "no-data";
      }

      const fields = [
        "statusBattery1",
        "statusBattery2",
        "statusDoor1",
        "statusDoor2",
        "statusPwr1",
        "statusPwr2",
        "statusFan1",
        "statusFan2",
        "statusHeater1",
        "statusHeater2",
        "statusCamAnpr1",
        "statusCamAnpr2",
        "statusCamAnpr3",
        "statusCamOvr1",
        "statusCamOvr2",
        "statusCamOvr3",
        "statusCamOvr4",
        "statusClw1",
        "statusClw2",
        "statusOverhight1",
        "statusOverhight2",
      ];

      const values = fields
        .map((field) => this.normalizeStatusValue(status[field]))
        .filter((value) => value !== null);

      if (!values.length) {
        return "no-data";
      }

      if (values.some((value) => value === 2)) {
        return "unavailable";
      }

      if (values.some((value) => value === 1)) {
        return "available";
      }

      return "no-data";
    },
    getAvailabilityLabel(status) {
      const state = this.getAvailabilityState(status);
      if (state === "available") {
        return this.$t("available");
      }
      if (state === "unavailable") {
        return this.$t("unavailable");
      }
      return this.$t("no-data");
    },
    getAvailabilityClass(status) {
      const state = this.getAvailabilityState(status);
      if (state === "available") {
        return "status-chip-ok";
      }
      if (state === "unavailable") {
        return "status-chip-bad";
      }
      return "status-chip-na";
    },
    toRows(status) {
      const labelMap = {
        routerIp: this.t("ls-router-ip"),
        statusBattery1: this.t("ls-battery-1"),
        statusBattery2: this.t("ls-battery-2"),
        statusDoor1: this.t("ls-door-1"),
        statusDoor2: this.t("ls-door-2"),
        statusPwr1: this.t("ls-pwr-1"),
        statusPwr2: this.t("ls-pwr-2"),
        statusFan1: this.t("ls-fan-1"),
        statusFan2: this.t("ls-fan-2"),
        statusHeater1: this.t("ls-heater-1"),
        statusHeater2: this.t("ls-heater-2"),
        statusTempIn1: this.t("ls-temp-in-1"),
        statusTempIn2: this.t("ls-temp-in-2"),
        statusHumidity1: this.t("ls-humidity-1"),
        statusHumidity2: this.t("ls-humidity-2"),
        statusUptime: this.t("ls-uptime"),
        statusTempOut1: this.t("ls-temp-out-1"),
        statusTempOut2: this.t("ls-temp-out-2"),
        statusCamAnpr1: this.t("ls-cam-anpr-1"),
        statusCamAnpr2: this.t("ls-cam-anpr-2"),
        statusCamAnpr3: this.t("ls-cam-anpr-3"),
        statusCamOvr1: this.t("ls-cam-ovr-1"),
        statusCamOvr2: this.t("ls-cam-ovr-2"),
        statusCamOvr3: this.t("ls-cam-ovr-3"),
        statusCamOvr4: this.t("ls-cam-ovr-4"),
        statusClw1: this.t("ls-clw-1"),
        statusClw2: this.t("ls-clw-2"),
        statusOverhight1: this.t("ls-overhight-1"),
        statusOverhight2: this.t("ls-overhight-2"),
        statusInternalVoltage: this.t("ls-internal-voltage"),
        statusBatteryVoltage: this.t("ls-battery-voltage"),
        statusBatteryCurrent: this.t("ls-battery-current"),
        statusPwrCurrent: this.t("ls-pwr-current"),
        sim1Signal: this.t("ls-sim1-signal"),
        sim1Rsrp: this.t("ls-sim1-rsrp"),
        sim1Band: this.t("ls-sim1-band"),
        sim2Signal: this.t("ls-sim2-signal"),
        sim2Rsrp: this.t("ls-sim2-rsrp"),
        sim2Band: this.t("ls-sim2-band"),
        createdAt: this.t("ls-created-at"),
      };

      const fields = [
        "routerIp",
        "statusBattery1",
        "statusBattery2",
        "statusDoor1",
        "statusDoor2",
        "statusPwr1",
        "statusPwr2",
        "statusFan1",
        "statusFan2",
        "statusHeater1",
        "statusHeater2",
        "statusTempIn1",
        "statusTempIn2",
        "statusHumidity1",
        "statusHumidity2",
        "statusUptime",
        "statusTempOut1",
        "statusTempOut2",
        "statusCamAnpr1",
        "statusCamAnpr2",
        "statusCamAnpr3",
        "statusCamOvr1",
        "statusCamOvr2",
        "statusCamOvr3",
        "statusCamOvr4",
        "statusClw1",
        "statusClw2",
        "statusOverhight1",
        "statusOverhight2",
        "statusInternalVoltage",
        "statusBatteryVoltage",
        "statusBatteryCurrent",
        "statusPwrCurrent",
        "sim1Signal",
        "sim1Rsrp",
        "sim1Band",
        "sim2Signal",
        "sim2Rsrp",
        "sim2Band",
        "createdAt",
      ];

      return fields
        .map((field) => ({
          label: labelMap[field] || field,
          value: field === "statusUptime" || field === "createdAt"
            ? this.formatDateTime(status[field])
            : this.formatValue(status[field]),
        }))
        // Pomijamy puste pola — nie pokazujemy wierszy z myślnikiem "-".
        .filter((row) => row.value !== "-");
    },
  },
  async mounted() {
    await this.loadLocations();
  },
};
</script>


<style scoped>
.row {
  margin: 0;
}

.content {
  margin-top: var(--spacing-lg);
  padding: var(--spacing-lg);
  background-color: var(--surface-primary);
  border: 1px solid var(--border-default);
  border-radius: var(--panel-radius);
  box-shadow: var(--shadow-panel);
}

.table-container button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 100px;
  min-height: var(--control-height-sm);
  padding: 0 var(--spacing-lg);
  background-color: var(--surface-primary);
  border: 1px solid var(--border-strong);
  border-radius: var(--control-radius);
  color: var(--text-primary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  letter-spacing: var(--letter-spacing-caps);
  cursor: pointer;
  text-align: center;
}

h5 {
  margin: 0;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  letter-spacing: var(--letter-spacing-caps);
  text-transform: uppercase;
  color: var(--text-primary);
}

.location-meta {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-regular);
  color: var(--text-muted);
}

.status-controls {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-md);
  align-items: center;
  margin: 15px 0 5px;
}

.status-search {
  max-width: 320px;
}

.status-sort {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: var(--spacing-sm);
}

.status-sort-label {
  /* jedna linia + wyśrodkowanie w pionie względem tekstu w kontrolkach obok */
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  min-height: var(--control-height);
  margin: 0;
  white-space: nowrap;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-medium);
  line-height: var(--line-height-normal);
}

.status-sort-select {
  max-width: 220px;
}

.status-sort-dir {
  padding: 7px 14px;
  background-color: var(--surface-selected);
  border: none;
  cursor: pointer;
  min-width: 44px;
}

.header-chip {
  margin-left: 10px;
  vertical-align: middle;
}

.location-actions {
  display: flex;
  gap: var(--spacing-sm);
  align-items: center;
}

.history-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-md);
}

.history-order-btn {
  padding: 4px 14px;
  background-color: var(--surface-selected);
  border: none;
  cursor: pointer;
}

.details-row-field {
  font-family: "Ubuntu", sans-serif;
  font-style: normal;
  font-weight: var(--font-weight-regular);
  font-size: var(--font-size-md);
}

.vehicle-panel {
  margin-top: 15px;
}

#road-info .row > div:first-child {
  border: 0;
}

table {
  width: 100%;
}

thead,
tbody {
  width: 100%;
}

.field-col {
  width: 35%;
}

.value-col {
  width: 65%;
}

.status-line {
  padding: 1rem;
  background: var(--surface-primary);
  border: 1px solid var(--border-subtle);
  margin-bottom: 1rem;
}

.status-line.error {
  color: var(--status-error-strong);
}

.status-section-title {
  margin: 0.5rem 0 0.75rem;
  font-weight: 700;
}

.lp-col {
  width: 80px;
}

.history-table {
  margin-bottom: 1rem;
}

.status-chip {
  display: inline-block;
  min-width: 110px;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
}

.status-chip-ok {
  background: var(--status-ok-soft);
  color: var(--status-ok-strong);
}

.status-chip-bad {
  background: var(--status-error-soft);
  color: var(--status-error-strong);
}

.status-chip-na {
  background: var(--surface-hover);
  color: var(--text-secondary);
}

.btn-download {
  border-radius: 4px;
}

.btn-download:hover {
  background-color: var(--border-default);
}
</style>
